<script>
import { EventBus, Helpdesk, Users } from '@/assets/js/helpers'

/*
 * The help desk: support.lassox.com (InstantDocs) inside the portal. Opened from
 * the help menu in the toolbar, or on its own for a product update.
 *
 * Views, kept as a stack so the back button retraces the way in:
 *   home        search, and the categories
 *   collection  one category's articles, and search within it
 *   article     one article
 *   contact     "Skriv til kundeservice", which creates a support ticket in HubSpot
 *   sent        the thank-you after sending
 *
 * Open it with EventBus.$emit('open-helpdesk', target), where target is
 * '/kb' (home), '/contact', or '/kb/articles/<InstantDocs id>'.
 *
 * Product updates: a help desk article that pops up once for the users it is
 * relevant to. An update pops up when the user has its feature (or it has
 * none), it was published after the user last checked product updates, and it
 * is between publishedAt and showUntil. Only the newest one shows; closing the
 * help desk, opening the article on support.lassox.com, or clicking its
 * button sets the user's lastProductUpdateCheck to now, so older unread
 * updates are skipped too.
 *
 * Someone who gets the feature after publishedAt still sees it, on their next
 * visit, until showUntil: by then it is no longer news.
 *
 *   articleId    the InstantDocs document id (from the editor URL, /doc/<id>)
 *   feature      a feature name from $store.state.features, or null for everyone
 *   publishedAt  when it goes out, local time: '2026-10-13T09:00'. A bare date
 *                ('2026-10-13') is read as midnight UTC, i.e. 01:00 or 02:00 in Denmark,
 *                and a trailing Z makes the time UTC.
 *   showUntil    optional, same format; defaults to 30 days after publishedAt
 *   action       optional { label, to }: a button under the article. `to` is a portal
 *                route, e.g. '/app/search', opened in a tab like the sidebar does.
 *                Clicking it counts as having seen the update.
 *
 * To review any article without touching the user's settings, open the portal
 * with ?helpdeskArticle=<articleId>. Its button shows too.
 */
const PRODUCT_UPDATES = [
  {
    articleId: 'cmv56t6ez001z5xfzijwhxwsm', // Ny og forbedret Målgruppesøgning
    feature: 'NewSearchApp',
    publishedAt: '2026-10-13T09:00:00.000Z',
    action: { label: 'Prøv den nye målgruppesøgning', to: '/app/search' },
  },
]

const SHOW_FOR_DAYS = 30
const showUntil = u => u.showUntil
  ? new Date(u.showUntil)
  : new Date(new Date(u.publishedAt).getTime() + SHOW_FOR_DAYS * 24 * 60 * 60 * 1000)

const SHOW_DELAY_MS = 2000
// Wait for a pause in typing before searching.
const SEARCH_DELAY_MS = 400
const GAP_PX = 24
const HELP_SITE = 'https://support.lassox.com'
const ARTICLE_ID = /^[a-z0-9]{20,32}$/
// A link to an article or category on the help site; the slug is the first group.
const HELP_LINK = /^https?:\/\/support\.lassox\.com\/help\/([^/?#]+)/i
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** Drops anything that can run script; links open in a new tab. */
const cleanArticleHtml = (html) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html')
  doc.querySelectorAll('script, style, link, meta, object, embed, form').forEach(el => el.remove())
  doc.querySelectorAll('*').forEach(el => {
    for (const attr of [...el.attributes]) {
      const unsafeUrl = ['href', 'src', 'action', 'formaction'].includes(attr.name) && /^\s*javascript:/i.test(attr.value)
      if (attr.name.startsWith('on') || unsafeUrl) el.removeAttribute(attr.name)
    }
  })
  // The steps are numbered by a CSS counter, which ignores <ol start>; carry it over.
  doc.querySelectorAll('ol[start]').forEach(ol => {
    const start = parseInt(ol.getAttribute('start'), 10)
    if (start > 1) ol.style.counterReset = `step ${start - 1}`
  })
  // InstantDocs gives headings their anchors in the browser, from the text: lower case, and
  // every run of anything but a-z and 0-9 a dash ("Målgruppesøgning: find" -> "m-lgruppes-gning-find").
  // A data attribute, not an id, so it can't clash with the portal's own ids.
  doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(h => {
    h.setAttribute('data-anchor', h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
  })
  doc.querySelectorAll('a[href]').forEach(a => {
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener')
  })
  return doc.body.innerHTML
}

export default {
  data () {
    return {
      visible: false,
      expanded: false,
      contentLeft: 0,
      stack: [],
      collections: null, // loaded once, on first visit to home
      // Product updates
      checked: false,
      update: null, // the PRODUCT_UPDATES entry shown; null when previewing or browsing
      autoShown: false, // popped up from PRODUCT_UPDATES, not opened by the user or ?helpdeskArticle
      // Contact form
      form: { name: '', email: '', message: '' },
      emailTouched: false, // the e-mail is only judged once the user has left the field
      sending: false,
      formError: null,
    }
  },
  computed: {
    user () { return this.$store.state.user },
    features () { return this.$store.state.features || [] },
    readOnly () { return !!this.$store.state.readOnly },
    // Support staff looking at a customer's account must not use up the customer's popup.
    canMarkSeen () { return !this.$store.state.impersonating && !this.$store.state.readOnly },
    lastCheck () {
      const settings = this.user.settings || {}
      return new Date(settings.lastProductUpdateCheck || this.user.created)
    },
    dueUpdate () {
      const now = new Date()
      return PRODUCT_UPDATES
        .filter(u => !u.feature || this.features.includes(u.feature))
        .filter(u => new Date(u.publishedAt) > this.lastCheck && new Date(u.publishedAt) <= now && now < showUntil(u))
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))[0] || null
    },
    view () { return this.stack[this.stack.length - 1] || null },
    canGoBack () { return this.stack.length > 1 },
    eyebrow () {
      switch (this.view.type) {
        case 'article': return (this.view.article && this.view.article.category) || 'Hjælp til Lasso'
        case 'contact':
        case 'sent': return 'Kundeservice'
        default: return 'Hjælp til Lasso'
      }
    },
    title () {
      switch (this.view.type) {
        case 'home': return 'Hvad kan vi hjælpe med?'
        case 'collection': return this.view.collection.title
        case 'article': return this.view.article ? this.view.article.title : ''
        case 'contact': return 'Skriv til kundeservice'
        case 'sent': return 'Tak for din besked'
        default: return ''
      }
    },
    subtitle () {
      switch (this.view.type) {
        case 'collection': return this.view.collection.description
        case 'contact': return 'Vi plejer at svare inden for en arbejdsdag.'
        default: return null
      }
    },
    /** The same page on support.lassox.com, for the "open" button. */
    siteUrl () {
      switch (this.view.type) {
        case 'home': return HELP_SITE
        case 'collection': return this.view.collection.url
        case 'article': return this.view.article && this.view.article.url
        default: return null
      }
    },
    articleHtml () { return this.view && this.view.article ? cleanArticleHtml(this.view.article.content) : '' },
    emailInvalid () {
      return this.emailTouched && !EMAIL.test(this.form.email.trim())
    },
    sender () {
      if (!this.user) return ''
      return this.user.name ? `${this.user.name} (${this.user.email})` : this.user.email
    },
    panelStyle () {
      if (!this.expanded) return null
      return { left: `${this.contentLeft + GAP_PX}px` }
    },
  },
  watch: {
    user: {
      immediate: true,
      handler (user) {
        if (!user || this.checked) return
        this.checked = true
        // Read the address bar, not $route: the router is abstract and only gets the page's
        // query after the store has initialised, which is after the user arrives (main.js).
        const preview = new URLSearchParams(window.location.search).get('helpdeskArticle')
        if (preview) this.showTimer = setTimeout(() => this.popUp(preview, null), SHOW_DELAY_MS)
        else if (this.canMarkSeen && this.dueUpdate) this.showTimer = setTimeout(() => this.popUp(this.dueUpdate.articleId, this.dueUpdate), SHOW_DELAY_MS)
      }
    },
  },
  mounted () {
    EventBus.$on('open-helpdesk', this.openFromTarget)
    this.measureContent()
    const content = document.getElementById('contentWrapper')
    if (content && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.measureContent())
      this.resizeObserver.observe(content)
    }
    window.addEventListener('resize', this.measureContent)
  },
  beforeDestroy () {
    EventBus.$off('open-helpdesk', this.openFromTarget)
    clearTimeout(this.showTimer)
    clearTimeout(this.searchTimer)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    window.removeEventListener('resize', this.measureContent)
  },
  methods: {
    /** The content area starts where the sidebar ends, pinned, collapsed or hidden. */
    measureContent () {
      const content = document.getElementById('contentWrapper')
      this.contentLeft = content ? content.getBoundingClientRect().left : 0
    },

    //#region Opening, closing and moving between views

    /** From the help menu, onboarding, notifications and apps. Old Groove article ids fall back to home. */
    openFromTarget (target) {
      const articleId = /^\/kb\/articles\/([^/?#]+)/.exec(target || '')
      if (target === '/contact') this.open([{ type: 'home' }, this.contactView()])
      else if (articleId && ARTICLE_ID.test(articleId[1])) this.open([{ type: 'home' }, { type: 'article', id: articleId[1] }])
      else this.open([{ type: 'home' }])
      this.track('Help desk opened', { View: this.view.type })
    },
    open (stack) {
      this.stack = []
      stack.forEach(view => this.push(view))
      this.measureContent()
      this.visible = true
    },
    push (view) {
      this.stack.push({ query: '', results: null, searching: false, error: null, ...view })
      this.load(this.view)
      this.$nextTick(() => {
        if (this.$refs.body) this.$refs.body.scrollTop = 0
        const focus = this.readOnly ? this.$refs.nameInput : this.$refs.messageInput
        if (focus && view.type === 'contact') focus.focus()
      })
    },
    back () {
      if (this.canGoBack) this.stack.pop()
    },
    home () {
      this.stack = this.stack.slice(0, 1)
    },
    close () {
      if (this.update) this.track('Product update closed')
      this.markSeen()
      this.visible = false
      this.update = null
      this.autoShown = false
    },

    /** A product update, or a preview of any article, opened on its own. */
    async popUp (articleId, update) {
      let article
      try {
        article = await Helpdesk.article(articleId)
      } catch (e) {
        // Not published yet, or InstantDocs is down: try again on the next visit.
        console.warn('Could not load help desk article', articleId, e)
        return
      }
      this.update = update
      this.autoShown = !!update
      // A preview has no entry of its own, but shows the button of the entry it matches.
      const entry = update || PRODUCT_UPDATES.find(u => u.articleId === articleId)
      this.open([{ type: 'home' }, { type: 'article', id: articleId, article, action: (entry && entry.action) || null }])
      this.track('Product update shown')
    },
    markSeen () {
      if (!this.update || !this.canMarkSeen) return
      const now = new Date()
      if (!this.user.settings) this.$set(this.user, 'settings', {})
      this.$set(this.user.settings, 'lastProductUpdateCheck', now)
      Users.updateSettings({ lastProductUpdateCheck: now }).catch(reason => console.warn('Failed to update user settings', reason))
      this.update = null
    },

    //#endregion

    //#region Loading

    async load (view) {
      view.error = null
      try {
        if (view.type === 'home' && !this.collections) this.collections = await Helpdesk.collections()
        if (view.type === 'collection' && !view.articles) this.$set(view, 'articles', await Helpdesk.collectionArticles(view.collection.id))
        if (view.type === 'article' && !view.article) {
          this.$set(view, 'article', await Helpdesk.article(view.id))
          this.track('Help desk article viewed', { ArticleId: view.article.id, Title: view.article.title })
          if (view.anchor) this.$nextTick(() => this.scrollToAnchor(view.anchor))
        }
      } catch (e) {
        console.warn('Could not load the help desk', e)
        view.error = 'Vi kunne ikke hente hjælpen lige nu. Prøv igen om lidt.'
      }
    },
    openCollection (collection) {
      this.push({ type: 'collection', collection })
    },
    openArticle (summary) {
      this.push({ type: 'article', id: summary.id })
    },
    onSearchInput (view) {
      clearTimeout(this.searchTimer)
      const query = view.query.trim()
      if (query.length < 2) {
        view.results = null
        view.searching = false
        return
      }
      view.searching = true
      this.searchTimer = setTimeout(async () => {
        const collectionId = view.type === 'collection' ? view.collection.id : null
        try {
          const results = await Helpdesk.search(query, collectionId)
          // Typing on while the request was out makes this answer stale.
          if (view.query.trim() !== query) return
          view.results = results
          this.track('Help desk searched', { Query: query, CollectionId: collectionId, Results: results.length })
        } catch (e) {
          console.warn('Help desk search failed', e && e.status, JSON.stringify(e && e.body))
          view.results = []
        } finally {
          if (view.query.trim() === query) view.searching = false
        }
      }, SEARCH_DELAY_MS)
    },

    /**
     * Links within the article scroll to their section; links to other help articles open
     * here, at their section if they name one; links into the portal open in a portal tab;
     * anything else opens in a new browser tab as usual.
     */
    async onArticleClick (event) {
      const link = event.target.closest('a[href]')
      if (!link) return
      // Articles link to the production portal; the route is the same on dev and staging.
      if (link.hostname === 'portal.lassox.com') {
        event.preventDefault()
        this.openPortalRoute(link.pathname + link.search + link.hash)
        return
      }
      const anchor = decodeURIComponent((link.hash || '').slice(1))
      const ownUrl = this.view.article.url
      if (link.getAttribute('href').startsWith('#') || (anchor && ownUrl && link.href.startsWith(ownUrl + '#'))) {
        event.preventDefault()
        this.scrollToAnchor(anchor)
        return
      }
      const slug = HELP_LINK.exec(link.href)
      if (!slug) return
      event.preventDefault()
      try {
        const target = await Helpdesk.resolve(slug[1])
        if (target.type === 'article' && target.id === this.view.id) this.scrollToAnchor(anchor)
        else if (target.type === 'article') this.push({ type: 'article', id: target.id, anchor })
        else {
          if (!this.collections) this.collections = await Helpdesk.collections()
          const collection = this.collections.find(c => c.id === target.id)
          if (collection) this.openCollection(collection)
          else window.open(link.href, '_blank', 'noopener')
        }
      } catch (e) {
        window.open(link.href, '_blank', 'noopener')
      }
    },
    /** Opens a portal route in a tab, like the sidebar; the help desk stays open beside it. */
    openPortalRoute (route) {
      this.$store.dispatch('openNewTab', this.$router.resolve(route).route)
      // Expanded, the help desk would hide the tab it just opened.
      this.expanded = false
      this.track('Help desk portal link clicked', { Route: route })
    },
    scrollToAnchor (anchor) {
      const body = this.$refs.body
      const target = anchor && body && [...body.querySelectorAll('[data-anchor]')].find(h => h.getAttribute('data-anchor') === anchor)
      if (!target) return
      body.scrollTo({ top: target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 16, behavior: 'smooth' })
    },

    //#endregion

    //#region Actions

    openOnSite () {
      window.open(this.siteUrl, '_blank', 'noopener')
      if (this.view.type === 'article' && this.update) {
        this.track('Product update opened in help desk')
        this.markSeen()
        this.visible = false
      }
    },
    runAction () {
      const action = this.view.action
      this.$store.dispatch('openNewTab', this.$router.resolve(action.to).route)
      this.track('Product update action clicked', { Action: action.label })
      this.markSeen()
      this.visible = false
    },

    contactView () {
      // A read-only account is shared: its stored name and e-mail are not the writer's.
      this.form = { name: '', email: '', message: '' }
      this.emailTouched = false
      this.formError = null
      return { type: 'contact' }
    },
    openContact () {
      this.push(this.contactView())
    },
    async send () {
      const message = this.form.message.trim()
      if (this.readOnly) {
        if (!this.form.name.trim()) return (this.formError = 'Skriv dit navn.')
        if (!EMAIL.test(this.form.email.trim())) {
          this.emailTouched = true
          return this.$refs.emailInput && this.$refs.emailInput.focus()
        }
      }
      if (!message) return (this.formError = 'Skriv en besked.')

      this.formError = null
      this.sending = true
      try {
        await Helpdesk.contact({
          message,
          // The router is abstract, so the address bar may lag behind; build the page from the route.
          url: window.location.origin + this.$route.fullPath,
          name: this.readOnly ? this.form.name.trim() : undefined,
          email: this.readOnly ? this.form.email.trim() : undefined,
        })
        this.track('Help desk message sent', { Path: this.$route.fullPath })
        this.stack.splice(this.stack.length - 1, 1, { type: 'sent' })
      } catch (e) {
        // A 400 carries the backend's own Danish validation message; anything else is ours to explain.
        this.formError = (e && e.status === 400 && e.body && e.body.errorMessage) || 'Beskeden blev ikke sendt. Prøv igen, eller skriv til contact@lassox.com.'
      } finally {
        this.sending = false
      }
    },

    track (event, traits = {}) {
      const article = this.view && this.view.article
      EventBus.$emit('amplitude:trackEvent', {
        event,
        traits: { ...(article ? { ArticleId: article.id, Title: article.title } : {}), AutoShown: this.autoShown, ...traits },
      })
    },

    //#endregion
  },
}
</script>

<template>
  <transition name="help-desk">
    <section
      v-if="visible && view"
      class="help-desk"
      :class="{ 'help-desk--expanded': expanded, 'help-desk--fit': view.type === 'article' || view.type === 'sent' }"
      :style="panelStyle"
      role="dialog"
      aria-labelledby="help-desk-title"
      @keydown.esc="close"
    >
      <header class="help-desk__header">
        <div class="help-desk__bar">
          <button
            v-if="canGoBack"
            type="button"
            class="help-desk__icon-button help-desk__back"
            title="Tilbage"
            aria-label="Tilbage"
            @click="back"
          >
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
            ><path d="M15 6l-6 6 6 6" /></svg>
          </button>

          <div class="help-desk__eyebrow">
            {{ eyebrow }}
          </div>

          <div class="help-desk__actions">
            <button
              type="button"
              class="help-desk__icon-button"
              :title="expanded ? 'Gør mindre' : 'Udvid'"
              :aria-label="expanded ? 'Gør mindre' : 'Udvid'"
              @click="expanded = !expanded"
            >
              <svg
                v-if="!expanded"
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
              <svg
                v-else
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" /></svg>
            </button>

            <button
              v-if="siteUrl"
              type="button"
              class="help-desk__icon-button"
              title="Åbn i Hjælp til Lasso"
              aria-label="Åbn i Hjælp til Lasso"
              @click="openOnSite"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>
            </button>

            <button
              type="button"
              class="help-desk__icon-button"
              title="Luk"
              aria-label="Luk"
              @click="close"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
        </div>

        <h2
          id="help-desk-title"
          class="help-desk__title"
        >
          {{ title }}
        </h2>
        <p
          v-if="subtitle"
          class="help-desk__subtitle"
        >
          {{ subtitle }}
        </p>

        <label
          v-if="view.type === 'home' || view.type === 'collection'"
          class="help-desk__search"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          ><circle
            cx="11"
            cy="11"
            r="7"
          /><path d="M20 20l-3.5-3.5" /></svg>
          <input
            v-model="view.query"
            type="search"
            :placeholder="view.type === 'collection' ? `Søg i ${view.collection.title}` : 'Søg efter artikler'"
            :aria-label="view.type === 'collection' ? `Søg i ${view.collection.title}` : 'Søg efter artikler'"
            @input="onSearchInput(view)"
          >
        </label>
      </header>

      <div
        ref="body"
        class="help-desk__body"
      >
        <p
          v-if="view.error"
          class="help-desk__note"
        >
          {{ view.error }}
        </p>

        <!-- Search results, on home or in a category -->
        <template v-else-if="(view.type === 'home' || view.type === 'collection') && view.query.trim().length >= 2">
          <p
            v-if="view.searching && !view.results"
            class="help-desk__note"
          >
            Søger…
          </p>
          <template v-else-if="view.results && view.results.length">
            <div class="help-desk__eyebrow help-desk__list-label">
              {{ view.results.length === 1 ? '1 artikel' : `${view.results.length} artikler` }}
            </div>
            <ul class="help-desk__list">
              <li
                v-for="result in view.results"
                :key="result.id"
              >
                <button
                  type="button"
                  class="help-desk__row"
                  @click="openArticle(result)"
                >
                  <span class="help-desk__row-title">{{ result.title }}</span>
                  <span
                    v-if="result.description"
                    class="help-desk__row-text"
                  >{{ result.description }}</span>
                  <span
                    v-if="view.type === 'home' && result.category"
                    class="help-desk__row-meta"
                  >{{ result.category }}</span>
                </button>
              </li>
            </ul>
          </template>
          <div
            v-else-if="view.results"
            class="help-desk__empty"
          >
            <p>Ingen artikler matcher «{{ view.query.trim() }}».</p>
            <button
              type="button"
              class="help-desk__link"
              @click="openContact"
            >
              Skriv til kundeservice
            </button>
          </div>
        </template>

        <!-- Home: the categories -->
        <template v-else-if="view.type === 'home'">
          <p
            v-if="!collections"
            class="help-desk__note"
          >
            Indlæser…
          </p>
          <template v-else>
            <div class="help-desk__eyebrow help-desk__list-label">
              Kategorier
            </div>
            <ul class="help-desk__list">
              <li
                v-for="collection in collections"
                :key="collection.id"
              >
                <button
                  type="button"
                  class="help-desk__row"
                  @click="openCollection(collection)"
                >
                  <span class="help-desk__row-title">{{ collection.title }}</span>
                  <span
                    v-if="collection.description"
                    class="help-desk__row-text"
                  >{{ collection.description }}</span>
                  <span class="help-desk__row-meta">{{ collection.articleCount === 1 ? '1 artikel' : `${collection.articleCount} artikler` }}</span>
                </button>
              </li>
            </ul>
            <div class="help-desk__contact-card">
              <p>Fandt du ikke svar?</p>
              <button
                type="button"
                class="help-desk__button help-desk__button--secondary"
                @click="openContact"
              >
                Skriv til kundeservice
              </button>
            </div>
          </template>
        </template>

        <!-- One category -->
        <template v-else-if="view.type === 'collection'">
          <p
            v-if="!view.articles"
            class="help-desk__note"
          >
            Indlæser…
          </p>
          <template v-else>
            <div class="help-desk__eyebrow help-desk__list-label">
              {{ view.articles.length === 1 ? '1 artikel' : `${view.articles.length} artikler` }}
            </div>
            <ul class="help-desk__list">
              <li
                v-for="article in view.articles"
                :key="article.id"
              >
                <button
                  type="button"
                  class="help-desk__row"
                  @click="openArticle(article)"
                >
                  <span class="help-desk__row-title">{{ article.title }}</span>
                  <span
                    v-if="article.description"
                    class="help-desk__row-text"
                  >{{ article.description }}</span>
                </button>
              </li>
            </ul>
          </template>
        </template>

        <!-- One article -->
        <template v-else-if="view.type === 'article'">
          <p
            v-if="!view.article"
            class="help-desk__note"
          >
            Indlæser…
          </p>
          <template v-else>
            <p
              v-if="view.article.description"
              class="help-desk__lead"
            >
              {{ view.article.description }}
            </p>
            <!-- eslint-disable-next-line vue/no-v-html -- cleaned by cleanArticleHtml -->
            <div
              class="help-desk__article"
              @click="onArticleClick"
              v-html="articleHtml"
            />
            <button
              v-if="view.action"
              type="button"
              class="help-desk__button help-desk__button--block"
              @click="runAction"
            >
              {{ view.action.label }}
            </button>
          </template>
        </template>

        <!-- Write to customer service -->
        <form
          v-else-if="view.type === 'contact'"
          class="help-desk__form"
          novalidate
          @submit.prevent="send"
        >
          <template v-if="readOnly">
            <label class="help-desk__field">
              <span>Navn</span>
              <input
                ref="nameInput"
                v-model="form.name"
                type="text"
                autocomplete="name"
                required
              >
            </label>
            <label class="help-desk__field">
              <span>E-mail</span>
              <input
                ref="emailInput"
                v-model="form.email"
                type="email"
                autocomplete="email"
                required
                :aria-invalid="emailInvalid ? 'true' : 'false'"
                aria-describedby="help-desk-email-error"
                :class="{ invalid: emailInvalid }"
                @blur="emailTouched = !!form.email.trim() || emailTouched"
              >
              <span
                v-if="emailInvalid"
                id="help-desk-email-error"
                class="help-desk__field-error"
              >Skriv en gyldig e-mail, fx navn@firma.dk.</span>
            </label>
          </template>
          <p
            v-else
            class="help-desk__note"
          >
            Vi svarer til {{ sender }}.
          </p>

          <label class="help-desk__field">
            <span>Besked</span>
            <textarea
              ref="messageInput"
              v-model="form.message"
              rows="6"
              placeholder="Hvad kan vi hjælpe med?"
              required
            />
          </label>

          <p
            v-if="formError"
            class="help-desk__error"
            role="alert"
          >
            {{ formError }}
          </p>

          <button
            type="submit"
            class="help-desk__button help-desk__button--block"
            :disabled="sending"
          >
            {{ sending ? 'Sender…' : 'Send besked' }}
          </button>
        </form>

        <!-- Sent -->
        <div
          v-else-if="view.type === 'sent'"
          class="help-desk__sent"
        >
          <p>Tak for din besked. Vi plejer at svare inden for en arbejdsdag.</p>
          <button
            type="button"
            class="help-desk__button help-desk__button--secondary"
            @click="home"
          >
            Tilbage til Hjælp til Lasso
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<style lang="less">
// Hand-matched to @lasso-x/ui (the portal is Vue 2 and can't use it): Poppins,
// 4/8px grid, 14px body, 14px dialog radius, --border #E2E2E2.
.help-desk {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  width: 560px;
  max-width: calc(100vw - 32px);
  // Browsing keeps one height, so the panel doesn't jump between views; an
  // article or the thank-you shrinks to fit (--fit).
  // Escaped: Less has its own min(), which would drop the window height at build time.
  height: ~"min(560px, calc(100vh - 48px))";
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  color: #262626;
  border: 1px solid #E2E2E2;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(178, 69, 15, 0.12), 0 2px 8px rgba(38, 38, 38, 0.06);
  font-family: Poppins, Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  transition: left 200ms ease, width 200ms ease;

  &--fit {
    height: auto;
    max-height: ~"min(560px, calc(100vh - 48px))";
  }

  &--expanded,
  &--expanded&--fit {
    width: auto; // left is set inline from where the content area starts
    height: auto;
    max-height: none;
    top: 24px;
  }

  // The right inset is 16px, not 24: the icon buttons' own 8px padding brings
  // the icons in line with the 24px left edge, and the title stops there too.
  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px 16px 24px;
    border-bottom: 1px solid #EAEAEA; // --border-soft
  }

  // Back, the category and the icons share one row, centred on each other.
  &__bar {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // Pulled into the 24px inset, so its arrow lines up with the text below.
  &__back {
    margin-left: -8px;
  }

  &__eyebrow {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #B2450F;
  }

  &__title {
    margin: 0 8px 0 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #262626;
  }

  &__subtitle {
    margin: -4px 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: #767676;
  }

  &__actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #767676;
    cursor: pointer;
    transition: background 120ms ease;

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    &:hover {
      background: #F1F1F1;
      color: #262626;
    }

    &:focus-visible {
      outline: 2px solid #FF7138;
      outline-offset: 0;
    }
  }

  // Search sits in the header so it stays put while the list scrolls.
  &__search {
    position: relative;
    display: block;
    margin: 4px 8px 0 0;

    svg {
      position: absolute;
      top: 12px;
      left: 12px;
      width: 16px;
      height: 16px;
      fill: none;
      stroke: #767676;
      stroke-width: 2;
      stroke-linecap: round;
      pointer-events: none;
    }

    input {
      width: 100%;
      height: 40px;
      padding: 0 12px 0 36px;
      border: 1px solid #E2E2E2;
      border-radius: 8px;
      background: #FFFFFF;
      color: #262626;
      font: inherit;
      outline: none;

      &::placeholder { color: #767676; }
      &:focus { border-color: #FFA07A; }
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 24px 24px;
  }

  &--expanded &__body > * {
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
  }

  &__note {
    margin: 0;
    color: #767676;
  }

  &__list-label {
    margin-bottom: 8px;
    color: #767676;
  }

  // Rows divided by rules, not boxed; the whole row is the button.
  &__list {
    margin: 0 -12px;
    padding: 0;
    list-style: none;

    li + li { border-top: 1px solid #EAEAEA; }
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: #F9F9F9;

      .help-desk__row-title { color: #B2450F; }
    }

    &:focus-visible {
      outline: 2px solid #FFA07A;
      outline-offset: -2px;
    }
  }

  &__row-title {
    font-weight: 500;
    color: #262626;
  }

  &__row-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 12px;
    line-height: 16px;
    color: #767676;
  }

  &__row-meta {
    font-size: 12px;
    line-height: 16px;
    color: #B2450F;
  }

  &__empty,
  &__sent {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    p { margin: 0; }
  }

  &__empty p { color: #767676; }

  &__contact-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 16px;
    padding: 16px;
    border-radius: 8px;
    background: #FFECE4; // --accent

    p { margin: 0; font-weight: 500; }
  }

  &__link {
    padding: 0;
    border: 0;
    background: none;
    color: #B2450F;
    font: inherit;
    text-decoration: underline;
    text-decoration-color: #FFCFB6;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover { text-decoration-color: currentColor; }
  }

  // Buttons: primary is --primary (peach.button) on white, h-10.
  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 8px;
    background: #FF7138;
    color: #FFFFFF;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: background 120ms ease;

    &:hover { background: #F25F24; }
    &:disabled { opacity: 0.6; cursor: default; }

    // The portal styles :focus globally; only keyboard focus gets a ring here.
    &:focus { outline: none; }
    &:focus-visible {
      outline: 2px solid #FFCFB6;
      outline-offset: 2px;
    }

    &--secondary {
      border: 1px solid #E2E2E2;
      background: #FFFFFF;
      color: #262626;

      &:hover { background: #F9F9F9; }
    }

    &--block {
      display: flex;
      width: 100%;
      margin-top: 24px;
    }
  }

  &--expanded &__button--block {
    width: auto;
    margin-left: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .help-desk__button--block { margin-top: 0; }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;

    span {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: #262626;
    }

    input,
    textarea {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #E2E2E2;
      border-radius: 8px;
      background: #FFFFFF;
      color: #262626;
      font: inherit;
      outline: none;

      &::placeholder { color: #767676; }
      &:focus { border-color: #FFA07A; }
      &.invalid { border-color: #EF4444; }
    }

    input { height: 40px; }
    textarea { resize: vertical; min-height: 120px; }
  }

  // Under the field; overrides the field label's span style.
  &__field &__field-error {
    font-weight: 400;
    color: #EF4444;
  }

  &__error {
    margin: 0;
    color: #EF4444;
  }

  &__lead {
    margin: 0 0 16px;
    color: #767676;
  }

  // The article body is InstantDocs' editor HTML.
  &__article {
    overflow-wrap: anywhere;

    p, ul, ol { margin: 0 0 12px; }
    // Set outright: a global reset in the portal strips list markers.
    ul, ol { padding-left: 24px; }
    ul { list-style: disc outside; }
    ul ul { list-style-type: circle; }
    li { display: list-item; margin-bottom: 4px; padding-left: 4px; }
    ul > li::marker { color: #FF7138; }

    // Numbered steps: the number in a peach circle. A counter, not ::marker,
    // since a marker can't be given a background.
    ol {
      list-style: none;
      padding-left: 0;
      counter-reset: step;
    }
    ol > li {
      position: relative;
      display: block;
      min-height: 24px;
      margin-bottom: 8px;
      padding: 2px 0 0 36px;
      counter-increment: step;

      &::before {
        content: counter(step);
        position: absolute;
        top: 0;
        left: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: #FFECE4;
        color: #B2450F;
        font-size: 12px;
        line-height: 16px;
        font-weight: 600;
      }
    }
    li > p { margin: 0; }
    h1, h2, h3, h4 {
      margin: 24px 0 8px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
    }
    b, strong { font-weight: 600; }
    a {
      color: #B2450F;
      text-decoration: underline;
      text-decoration-color: #FFCFB6;
      text-underline-offset: 2px;

      &:hover { text-decoration-color: currentColor; }
    }
    img, video {
      display: block;
      max-width: 100% !important;
      height: auto;
      margin: 16px auto !important;
      border: 1px solid #E2E2E2 !important;
      border-radius: 8px !important;
    }
    iframe { max-width: 100%; border: 0; border-radius: 8px; }
    > :last-child { margin-bottom: 0; }
  }
}

.help-desk-enter-active,
.help-desk-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.help-desk-enter,
.help-desk-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

@media (prefers-reduced-motion: reduce) {
  .help-desk,
  .help-desk-enter-active,
  .help-desk-leave-active {
    transition: none;
  }
}
</style>
